import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

/* Legal Engine document renderer — Stripe/Notion-grade reading experience:
   sticky lateral ToC, anchor deep-links, smooth scroll, perfect typography.
   Renders any registry document (static or resolved templated). */

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history?.replaceState) window.history.replaceState(null, "", `#${id}`);
};

const Blocks = ({ blocks }) => {
  const out = [];
  let list = [];
  let fields = [];
  const flushList = () => {
    if (list.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="flex flex-col gap-2 my-3">
          {list.map((b, i) => (
            <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-[#2b2b2b]">
              <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#1D4D3B] shrink-0" aria-hidden="true" />
              <span>{b.text}</span>
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  const flushFields = () => {
    if (fields.length) {
      out.push(
        <dl key={`dl-${out.length}`} className="my-4 rounded-2xl border border-black/8 divide-y divide-black/5 overflow-hidden" data-testid="legal-particulars-grid">
          {fields.map((b, i) => (
            <div key={i} className="grid sm:grid-cols-[220px_1fr] gap-1 sm:gap-6 px-5 py-3.5 bg-white even:bg-[#FAFAF9]">
              <dt className="text-[13px] font-semibold text-[#0A0A0A]/60">{b.label}</dt>
              <dd className="text-[15px] text-[#0A0A0A]">{b.text}</dd>
            </div>
          ))}
        </dl>
      );
      fields = [];
    }
  };
  for (const b of blocks) {
    if (b.t === "li") {
      flushFields();
      list.push(b);
    } else if (b.t === "field") {
      flushList();
      fields.push(b);
    } else {
      flushList();
      flushFields();
      out.push(
        <p key={`p-${out.length}`} className="text-[15px] leading-[1.75] text-[#2b2b2b] my-3">
          {b.text}
        </p>
      );
    }
  }
  flushList();
  flushFields();
  return out;
};

const Toc = ({ doc, activeId, onGo }) => {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("legal.toc")} className="flex flex-col gap-5" data-testid="legal-toc">
      {doc.parts.map((part) => (
        <div key={part.id} className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => onGo(part.id)}
            className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/45 hover:text-[#0A0A0A] transition-colors duration-200"
          >
            {part.title}
          </button>
          {part.sections.filter((s) => s.title).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onGo(s.id)}
              data-testid={`legal-toc-${s.id}`}
              className={`text-left text-[13px] leading-snug py-0.5 transition-colors duration-200 ${
                s.level === 2 ? "pl-4" : "pl-1"
              } ${activeId === s.id ? "text-[#1D4D3B] font-medium" : "text-[#0A0A0A]/60 hover:text-[#0A0A0A]"}`}
            >
              {s.number && <span className="tabular-nums mr-1.5">{s.number}</span>}
              {s.title}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
};

export const LegalDocView = ({ doc, headerExtra = null }) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const sectionIds = useMemo(
    () => doc.parts.flatMap((p) => p.sections.map((s) => s.id)),
    [doc]
  );

  useEffect(() => {
    // scroll-spy for the lateral ToC
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-10% 0px -75% 0px" }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    if (window.location.hash) {
      setTimeout(() => scrollTo(window.location.hash.slice(1)), 150);
    }
    return () => observer.disconnect();
  }, [sectionIds]);

  const go = (id) => {
    setTocOpen(false);
    scrollTo(id);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 pb-24" data-testid={`legal-doc-${doc.key}`}>
      <header className="pt-14 pb-8 border-b border-black/5 mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1D4D3B] mb-3">{t("legal.eyebrow")}</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A]">{doc.title}</h1>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="inline-flex items-center rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-[#0A0A0A]/70" data-testid="legal-doc-version">
            {t("legal.version", { version: doc.version })}
          </span>
          {doc.pinned && (
            <span className="inline-flex items-center rounded-full bg-[#1D4D3B]/10 px-3 py-1 text-xs font-medium text-[#1D4D3B]" data-testid="legal-doc-pinned">
              {t("legal.pinned")}
            </span>
          )}
          {headerExtra}
        </div>
      </header>

      {/* mobile ToC */}
      <div className="lg:hidden mb-6">
        <button
          type="button"
          data-testid="legal-toc-toggle"
          onClick={() => setTocOpen((v) => !v)}
          className="w-full flex items-center justify-between rounded-2xl border border-black/10 px-5 py-3.5 text-sm font-medium text-[#0A0A0A]"
        >
          {t("legal.toc")}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${tocOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {tocOpen && (
          <div className="mt-3 rounded-2xl border border-black/5 bg-[#FAFAF9] p-5 max-h-80 overflow-y-auto">
            <Toc doc={doc} activeId={activeId} onGo={go} />
          </div>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-14">
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 pb-8">
            <Toc doc={doc} activeId={activeId} onGo={go} />
          </div>
        </aside>
        <article>
          {doc.parts.map((part) => (
            <section key={part.id} id={part.id} className="scroll-mt-28 mb-12">
              <h2 className="text-[13px] font-semibold uppercase tracking-[0.16em] text-[#1D4D3B] border-b border-black/5 pb-3 mb-5">
                {part.title}
              </h2>
              {part.sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-28 mb-7">
                  {s.title && (
                    <h3 className={`font-semibold tracking-tight text-[#0A0A0A] mb-1 ${s.level === 2 ? "text-[15px]" : "text-lg"}`}>
                      {s.number && <span className="tabular-nums text-[#0A0A0A]/40 mr-2">{s.number}</span>}
                      {s.title}
                    </h3>
                  )}
                  <Blocks blocks={s.blocks} />
                </section>
              ))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
};
